export const NoContext = 0;
export const SystemContext = 1;
export const GroupContext = 2;
export const UserContext = 3;

export const DefaultDrawDowns = 1;

export const ThreeMinutes = 180000;
export const EightMB = 8 * 1024 * 1024;
export const PublicAssetsUrl = "https://inovus-public-assets.s3.eu-west-2.amazonaws.com";

/**
 * Contexts.
 */
export enum CONTEXT {
    NoContext = "None",
    SystemContext = "System",
    GroupContext = "Group",
    UserContext = "User",
}

/**
 * Colours.
 */

export enum COLOURS {
    Blue = "#1990FF",
    Cabaret = "#C75167",
    Green = "#36c361",
    Grey = "#707070",
    Lavender = "#CCCCFF",
    Lochmara = "#47658F",
    Sail = "#a1c8eb",
    Steel = "#457db0",
}

export enum BRAND_COLOURS {
    Hologic = "#151F6D",
    Inovus = "#001529",
}

export enum IModalState {
    Standard = "standard",
    Continue = "continue",
    Uploading = "uploading",
    Complete = "complete",
    Error = "error",
}

export enum IUploadState {
    Aborted = "aborted",
    Completed = "completed",
    Created = "created",
    Initiated = "initiated",
    Idle = "idle",
    Pending = "pending",
    Error = "error",
}

/**
 * Query keys.
 */
export enum QueryKeys {
    Achievement = "achievement",
    ActivityCurrent = "activityCurrent",
    ActivityCurrentPublic = "activityCurrentPublic",
    ActivityDefinitions = "activityDefinitions",
    ActivityListUser = "activityListUser",
    ActivityListPublic = "activityListPublic",
    ActivityListConnections = "activityListConnections",
    ActivityProgression = "activityProgression",
    ActivitySearchResults = "activitySearchResults",
    ActivitySubmissions = "activitySubmissions",
    AdminLicenses = "adminLicenses",
    AssessmentInstanceList = "assessmentInstanceList",
    AssessmentList = "assessmentList",
    AssessmentReport = "assessmentReport",
    AssessmentUserInstanceList = "assessmentUserInstanceList",
    Automation = "automation",
    Compliance = "compliance",
    Downloads = "downloads",
    ExportMetadata = "exportMetadata",
    FeatureFlag = "featureFlag",
    GroupMembers = "groupMembers",
    GroupMembersFiltered = "groupMembersFiltered",
    GroupUserAddresses = "groupUserAddresses",
    GroupUserCredits = "groupUserCredits",
    GroupUserLicenses = "groupUserLicenses",
    GroupUserRoles = "groupUserRoles",
    Notifications = "notifications",
    PointsReport = "pointsReport",
    PointsTableData = "pointsTableData",
    Report = "report",
    Roles = "roles",
    Templates = "templates",
    Terms = "terms",
    QuestionnaireCurrent = "questionnaireCurrent",
    QuestionnaireInstanceCurrent = "questionnaireInstanceList",
    QuestionnaireAssociationList = "questionnaireAssociationList",
    QuestionnaireList = "questionnaireList",
    UserAcceptedTerms = "userAcceptedTerms",
    UserAccountDetails = "userAccountDetails",
    UserAccountDetailsPrivate = "userAccountDetailsPrivate",
    UserConnections = "userConnections",
    UserCredits = "userCredits",
    UserGroupAddresses = "userGroupAddresses",
    UserGroupCredits = "userGroupCredits",
    UserGroupLicenses = "userGroupLicenses",
    UserGroups = "userGroups",
    UserOptions = "userOptions",
    UserMemberships = "userMemberships",
    UserPrompts = "userPrompts",
    UserSearch = "userSearch",
    UserSearchRoles = "userSearchRoles",
    UserSearchAddresses = "userSearchAddresses",
    UserSearchCredits = "userSearchCredits",
    UserSearchLicenses = "userSearchLicenses",
}

/**
 * Paths allowed to be accessed without authentication.
 */
export enum UnauthenticatedPath {
    Login = "login",
    Register = "register",
    Activate = "activate",
    ResetPassword = "reset-password", // The route to request a password reset
    PasswordReset = "password-reset", // The route to complete a password reset
}

/**
 * Achievement types
 */
export enum Achievement {
    Profile = "user-account-completion",
}

/**
 * Inovus Totum usage terms.
 */
export const TotumTerms = {
    // EULA Section
    title: "End-User License Agreement (EULA) of Totum by Inovus Medical",
    legalAgreement: 'This End-User License Agreement ("EULA") is a legal agreement between you and Inovus Ltd',
    paragraph1: `This EULA agreement governs your acquisition and use of our Totum software ("Software") directly from Inovus Ltd
    or indirectly through an Inovus Ltd authorised reseller or distributor (a "Reseller").`,
    paragraph2: `Please read this EULA agreement carefully before completing the installation process and using the Totum
    software. It provides a license to use the Totum software and contains warranty information and liability
    disclaimers.`,
    paragraph3: `If you register for a free trial of the Totum software, this EULA agreement will also govern that trial. By
    clicking "accept" or installing and/or using the Totum software, you are confirming your acceptance of the
    Software and agreeing to become bound by the terms of this EULA agreement.`,
    paragraph4: `If you are entering into this EULA agreement on behalf of a company or other legal entity, you represent that you
    have the authority to bind such entity and its affiliates to these terms and conditions. If you do not have such
    authority or if you do not agree with the terms and conditions of this EULA agreement, do not install or use the
    Software, and you must not accept this EULA agreement.`,
    paragraph5: `This EULA agreement shall apply only to the Software supplied by Inovus Ltd herewith regardless of whether other
    software is referred to or described herein. The terms also apply to any Inovus Ltd updates, supplements,
    Internet-based services, and support services for the Software, unless other terms accompany those items on
    delivery. If so, those terms apply.`,
    licenseGrant: "License Grant",
    paragraph6: `Inovus Ltd hereby grants you a personal, non-transferable, non-exclusive licence to use the Totum software on
  your devices in accordance with the terms of this EULA agreement.</br>
  You are permitted to load the Totum software (for example a PC, laptop, mobile or tablet) under your control.
  You are responsible for ensuring your device meets the minimum requirements of the Totum software.`,
    notPermittedTo: "You are not permitted to:",
    opt1: `Edit, alter, modify, adapt, translate or otherwise change the whole or any part of the Software nor permit the
  whole or any part of the Software to be combined with or become incorporated in any other software, nor
  decompile, disassemble or reverse engineer the Software or attempt to do any such things`,
    opt2: `Reproduce, copy, distribute, resell or otherwise use the Software for any commercial purpose`,
    opt3: `Allow any third party to use the Software on behalf of or for the benefit of any third party`,
    opt4: `Use the Software in any way which breaches any applicable local, national or international law`,
    opt5: `use the Software for any purpose that Inovus Ltd considers is a breach of this EULA agreement`,
    intellectualProperty: "Intellectual Property and Ownership",
    paragraph7: `Inovus Ltd shall at all times retain ownership of the Software as originally downloaded by you and all subsequent
  downloads of the Software by you. The Software (and the copyright, and other intellectual property rights of
  whatever nature in the Software, including any modifications made thereto) are and shall remain the property of
  Inovus Ltd.`,
    paragraph8: `Inovus Ltd reserves the right to grant licences to use the Software to third parties.`,
    termination: "Termination",
    paragraph9: `This EULA agreement is effective from the date you first use the Software and shall continue until terminated.
  You may terminate it at any time upon written notice to Inovus Ltd.`,
    paragraph10: `It will also terminate immediately if you fail to comply with any term of this EULA agreement. Upon such
  termination, the licenses granted by this EULA agreement will immediately terminate and you agree to stop all
  access and use of the Software. The provisions that by their nature continue and survive will survive any
  termination of this EULA agreement.`,
    governingLaw: "Governing Law",
    paragraph11: `This EULA agreement, and any dispute arising out of or in connection with this EULA agreement, shall be governed
  by and construed in accordance with the laws of GB.`,

    // Privacy Policy Section
    privacyTitle: "Privacy Policy",
    privacyEffectiveDate: "Effective Date: March 24, 2025",
    privacyIntro: `Totum is a surgical training and simulation platform developed and operated by Inovus Medical. Inovus Medical is a manufacturer of surgical training technologies.`,
    privacyParagraph1: `We understand that you are aware of and care about your own personal privacy interests, and we take that seriously. This Privacy Policy describes Totum's policies and practices regarding its collection and use of your personal data, and sets forth your privacy rights. We recognize that information privacy is an ongoing responsibility, and so we will from time to time update this Privacy Policy as we undertake new personal data practices or adopt new privacy policies.`,

    privacyDPOTitle: `Data Protection Officer`,
    privacyParagraph2: `Totum is developed and operated by Inovus Medical, which is headquartered in St Helens in the United Kingdom. Inovus Medical has appointed an internal data protection officer for you to contact if you have any questions or concerns about Totum's personal data policies or practices. If you would like to exercise your privacy rights, please direct your query to Inovus Medical's data protection officer. The data protection officer's name and contact information are as follows:`,
    privacyDPOInfo: `Jordan Van Flute
Inovus Medical
Unit 14 Wharton Street, St Helens, WA9 5AA

DPO@Inovusmedical.com

01744752952`,

    privacyDataUseTitle: `How we collect and use (process) your personal information`,
    privacyParagraph3: `Totum collects personal information about its users. This information includes:`,

    privacyDataCategories: {
        authentication: {
            title: "Internal Authentication Data",
            items: ["Email address", "Password information (securely stored)"],
        },
        identifying: {
            title: "Identifying Information",
            items: ["Full name", "Nickname", "Gender (if provided)"],
        },
        demographic: {
            title: "Demographic Information",
            items: ["Date of birth", "Address", "Years of training", "Expected Graduation Date"],
        },
        professional: {
            title: "Professional Information",
            items: ["Medical school", "Organisation", "Specialty"],
        },
        behavioral: {
            title: "Behavioral Data",
            items: [
                "Measured surgical metrics captured within our simulator software",
                "Activities and procedures performed",
                "Performance metrics",
            ],
        },
        social: {
            title: "Social Network Information",
            items: ["Connections with other users on the platform", "Group memberships", "Assessment information"],
        },
        communication: {
            title: "Communication Data",
            items: ["Assessments set and received by you", "Video comments received by you"],
        },
    },

    privacyDataUses: [
        "Provide you with access to the Totum platform and its features",
        "Analyze and improve surgical training abilities using psychomotor metrics",
        "Allow you to connect with other practitioners in your field",
        "Facilitate educational assessments and feedback",
        "Improve our platform and services",
    ],

    privacyParagraph4: `We do not sell personal information to anyone and only share it with third parties who are facilitating the delivery of our services.`,

    privacyWebsiteUseTitle: `Use of the Totum Website`,
    privacyWebsiteUseParagraph: `As is true of most other websites, Totum's website collects certain information automatically and stores it in log files. The information may include internet protocol (IP) addresses, the region or general location where your computer or device is accessing the internet, browser type, operating system and other usage information about the use of Totum's website, including a history of the pages you view. We use this information to help us design our site to better suit our users' needs. We may also use your IP address to help diagnose problems with our server and to administer our website, analyze trends, track visitor movements, and gather broad demographic information that assists us in identifying visitor preferences.

Totum has a legitimate interest in understanding how members, customers and potential customers use its website. This assists Totum with providing more relevant products and services, with communicating value to our sponsors and corporate members, and with providing appropriate staffing to meet member and customer needs.`,

    privacyCookiesTitle: `Cookies and tracking technologies`,
    privacyCookiesParagraph: `Totum makes available a comprehensive cookies policy that describes the cookies and tracking technologies used on Totum website and provides information on how users can accept or reject them. To view the notice, just click 'cookies policy'.`,

    privacySharingTitle: `Sharing information with third parties`,
    privacySharingParagraph1: `The personal information Totum collects from you is stored in one or more databases hosted by third parties located in the United Kingdom. These third parties do not use or have access to your personal information for any purpose other than cloud storage and retrieval. On occasion, Totum engages third parties to send information to you, including information about our products, services, and events.`,

    privacySharingParagraph2: `We do not otherwise reveal your personal data to non-Inovus Medical persons or businesses for their independent use unless:`,
    privacySharingConditions: [
        "you request or authorise it;",
        "it's in connection with Totum hosted and Totum co-sponsored conferences;",
        "the information is provided to comply with the law (for example, compelled by law enforcement to comply with a search warrant, subpoena, or court order), enforce an agreement we have with you, or to protect our rights, property or safety, or the rights, property or safety of our employees or others;",
        "the information is provided to our agents, vendors or service providers who perform functions on our behalf;",
        "to address emergencies or acts of God; or",
        "to address disputes, claims, or to persons demonstrating legal authority to act on your behalf.",
    ],

    privacySharingParagraph3: `We may also gather aggregated data about our services and website visitors and disclose the results of such aggregated (but not personally identifiable) information to our partners, service providers, advertisers, and/or other third parties for marketing or promotional purposes.`,

    privacyRightsTitle: `Data Subject rights`,
    privacyRightsParagraph1: `The European Union's General Data Protection Regulation (GDPR) and other countries' privacy laws provide certain rights for data subjects. Data Subject rights under GDPR include the following:`,
    privacyRightsList: [
        "Right to be informed",
        "Right of access",
        "Right to rectification",
        "Right to erasure",
        "Right to restrict processing",
        "Right of data portability",
        "Right to object",
        "Rights related to automated decision making including profiling",
    ],

    privacyRightsParagraph2: `This Privacy Policy is intended to provide you with information about what personal data Totum collects about you and how it is used.`,

    privacyRightsParagraph3: `If you wish to confirm that Totum is processing your personal data, or to have access to the personal data Totum may have about you, please contact us.`,

    privacyRightsParagraph4: `You may also request information about:`,
    privacyInfoRequestOptions: [
        "the purpose of the processing;",
        "the categories of personal data concerned;",
        "who else outside Totum might have received the data;",
        "what the source of the information was (if you didn't provide it directly to Totum);",
        "how long it will be stored.",
    ],

    privacyRightsParagraph5: `You have a right to correct (rectify) the record of your personal data maintained by Totum if it is inaccurate. You may request that Totum erase that data or cease processing it, subject to certain exceptions. You may also request that Totum cease using your data for direct marketing purposes. In many countries, you have a right to lodge a complaint with the appropriate data protection authority if you have concerns about how Totum processes your personal data. When technically feasible, Totum will at your request provide your personal data to you.`,

    privacyRightsParagraph6: `Reasonable access to your personal data will be provided at no cost. If access cannot be provided within a reasonable time frame, Totum will provide you with a date when the information will be provided. If for some reason access is denied, Totum will provide an explanation as to why access has been denied.`,

    privacyRightsParagraph7: `For questions or complaints concerning the processing of your personal data, you can email us at privacy@inovusmedical.com. Alternatively, if you are located in the European Union, you can also have recourse to the European Data Protection Supervisor or with your nation's data protection authority.`,

    privacyForgottenTitle: `Your right to be forgotten`,
    privacyForgottenParagraph: `You may request the removal of your data at any time, but note that this will remove your identifying data from our database completely and you will no longer be able to access our platforms and services.`,

    privacyStorageTitle: `Data storage and retention`,
    privacyStorageParagraph: `Your personal data is stored by Totum on its servers, and on the servers of the cloud-based database management services Totum engages, located in the United Kingdom. Totum retains service data for the duration of the customer's business relationship with Totum and for a period of time thereafter, to analyze the data for Totum's own operations, and for historical and archiving purposes associated with Totum's services. Totum retains prospect data until such time as it no longer has business value and is purged from Totum's systems. All personal data that Totum controls may be deleted upon verified request from Data Subjects or their authorized agents. For more information on where and how long your personal data is stored, and for more information on your rights of erasure and portability, please contact us at: privacy@inovusmedical.com`,

    privacyChildrenTitle: `Children's data`,
    privacyChildrenParagraph: `We do not knowingly attempt to solicit or receive information from children under the age of 18.`,

    privacyContactTitle: `Questions, concerns or complaints`,
    privacyContact: `If you have questions, concerns, complaints, or would like to exercise your rights, please contact us at:

Inovus Medical (Regarding Totum)
Jordan Van Flute
Unit 14 Wharton Street
St Helens
WA9 5AA

privacy@inovusmedical.com

01744752952`,
};

/**
 * Local Storage Keys.
 */
export enum LocalStorageKeys {
    ActivityUploads = "ActivityUploads",
}

/**
 * Terms types.
 */
export enum TermsTypes {
    Terms = "terms-and-conditions",
    PrivacyPolicy = "privacy-policy",
}

/**
 * Prompt types.
 */
export enum PromptTypes {
    Terms = "terms-and-conditions",
    PrivacyPolicy = "privacy-policy",
    GraduationYear = "graduation-year",
}

/**
 * UserPrompt Status types.
 */
export enum UserPromptStatus {
    Dismissed = "dismissed",
    Completed = "completed",
}

/**
 * Map term types to their display titles.
 */
export const TermsTitles: Record<string, string> = {
    [TermsTypes.Terms]: "Terms and Conditions",
    [TermsTypes.PrivacyPolicy]: "Privacy Policy",
};

export const TermsConfig = [
    {
        type: TermsTypes.Terms,
        label: TermsTitles[TermsTypes.Terms],
    },
    {
        type: TermsTypes.PrivacyPolicy,
        label: TermsTitles[TermsTypes.PrivacyPolicy],
    },
];
